import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Checkbox,
    Label,
    Modal,
    SegmentedControl,
    SparkIcon,
    SparkModalPortal,
    Text,
    TooltipTrigger,
    sparkToast,
} from '@spark';
import { AddDataModal, useDataFetching } from '@/features/datasets';
import { data_from_server_post } from '@/shared/http/client';
import { getDefaultTrainingParams } from '@/features/trainings/constants';
import { startTrainingProcess } from '@/features/trainings/utils/trainingUtils';
import { GlobalContext } from '@/app/providers/globalState';
import ImproveFlowAiStep, { hasPromptText } from './ImproveFlowAiStep';
import ImproveFlowAugmentationStep from './ImproveFlowAugmentationStep';
import ImproveFlowHumanStep, { IMPROVE_MODAL_TABLE_PAGE_SIZE } from './ImproveFlowHumanStep';
import { areAllClassificationReviewGoalsMet } from '@/features/datasets/utils/improvementModeReviewGoals';
import {
    collectImproveAugmentationItems,
    progressBelongsToUsecase,
} from './improveAugmentationItems';

const STACK_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-16)',
};

const ANNOTATE_MODE_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--dimension-spacing-08)',
};

const ANNOTATE_TITLE_ROW_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--dimension-spacing-08)',
};

const ANNOTATE_ITEMS = [
    { id: 'human', label: 'Human Annotation' },
    { id: 'ai', label: 'AI Annotation' },
];

const SPARK_MODAL_PORTAL_MASK_CLASS = 'spark-modal-portal-mask';

function isSparkModalMask(target) {
    return target instanceof Element && target.classList.contains(SPARK_MODAL_PORTAL_MASK_CLASS);
}

function defaultTrainingParamsForUsecase(usecase) {
    const defaults = getDefaultTrainingParams();
    const hasBenchmarks = (usecase?.benchmarks || []).length > 0;
    return {
        train: hasBenchmarks ? 80 : defaults.DEFAULT_TRAIN_RATIO,
        validation: hasBenchmarks ? 20 : defaults.DEFAULT_VALIDATION_RATIO,
        metricOptimization: defaults.DEFAULT_METRIC_OPTIMIZATION,
        betaForFScore: 1,
        trainingDevice: 'gpu',
        predictionStrategy: 'automatic',
    };
}

export default function ImproveFlowModal({ open, onClose, usecase }) {
    const { updateUsecase, progressStatus, allUsecases } = useContext(GlobalContext) || {};
    const liveUsecase = useMemo(() => {
        if (!usecase?._id || !Array.isArray(allUsecases)) return usecase;
        return allUsecases.find((item) => item?._id === usecase._id) || usecase;
    }, [allUsecases, usecase]);
    const [step, setStep] = useState('annotate');
    const [annotationMode, setAnnotationMode] = useState('human');
    const [prompt, setPrompt] = useState(usecase?.prompt || '');
    const [systemPrompt, setSystemPrompt] = useState(usecase?.teacher_system_prompt || '');
    const [emailMe, setEmailMe] = useState(true);
    const [currentFilters, setCurrentFilters] = useState({});
    const [activeSorter, setActiveSorter] = useState(null);
    const [allLabelOptions, setAllLabelOptions] = useState({});
    const [tableOverlayOpen, setTableOverlayOpen] = useState(false);
    const [addDataSource, setAddDataSource] = useState(null);
    const [droppedFiles, setDroppedFiles] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [improveTourDismissed, setImproveTourDismissed] = useState(false);
    const [humanReviewProgress, setHumanReviewProgress] = useState({});
    const [focusIncompleteToken, setFocusIncompleteToken] = useState(0);
    const tablePopoverOpenRef = useRef(false);
    const suppressWizardCloseRef = useRef(false);
    const [baselineDatasets, setBaselineDatasets] = useState([]);
    const [sessionProgress, setSessionProgress] = useState({});

    const addDataOpen = Boolean(addDataSource);
    const nestedOverlayOpen = tableOverlayOpen || addDataOpen;

    const {
        dataset,
        datasetMetadata,
        availableExtraParamsKeys,
        crossReferenceFields,
        isDatasetLoading,
        handleTableChange,
        setIsInDB,
        updateRecord,
    } = useDataFetching(open ? liveUsecase : null, open, currentFilters, activeSorter, IMPROVE_MODAL_TABLE_PAGE_SIZE);

    useEffect(() => {
        if (!open) {
            setSessionProgress({});
            setBaselineDatasets([]);
            return undefined;
        }
        setBaselineDatasets([...(liveUsecase?.datasets || [])]);
        setStep('annotate');
        setAnnotationMode('human');
        setPrompt(liveUsecase?.prompt || '');
        setSystemPrompt(liveUsecase?.teacher_system_prompt || '');
        setEmailMe(true);
        setCurrentFilters({});
        setActiveSorter(null);
        setAddDataSource(null);
        setDroppedFiles(null);
        setSubmitting(false);
        setImproveTourDismissed(false);
        setFocusIncompleteToken(0);
        tablePopoverOpenRef.current = false;
        suppressWizardCloseRef.current = false;
        return undefined;
    }, [open, liveUsecase?._id]);

    useEffect(() => {
        if (open) return undefined;
        setHumanReviewProgress({});
        return undefined;
    }, [open]);

    useEffect(() => {
        if (!open || !liveUsecase?._id) return undefined;
        data_from_server_post('get-labels', liveUsecase._id, (labels) => {
            setAllLabelOptions(labels || {});
        });
        return undefined;
    }, [open, liveUsecase?._id]);

    useEffect(() => {
        if (!open || !liveUsecase?._id) return undefined;
        setSessionProgress((previous) => {
            let changed = false;
            const next = { ...previous };
            Object.entries(progressStatus || {}).forEach(([key, entry]) => {
                if (!progressBelongsToUsecase(key, entry, liveUsecase._id) || entry?.isBenchmark) {
                    return;
                }
                if (next[key] !== entry) {
                    next[key] = entry;
                    changed = true;
                }
            });
            // A key removed from the global status while still in flight means
            // the job was cancelled (e.g. Stop on the floating bar) — drop it
            // so the modal doesn't show an eternal in-progress row. Terminal
            // entries stay visible for the session even after being dismissed.
            Object.entries(previous).forEach(([key, entry]) => {
                if ((progressStatus || {})[key]) return;
                if (entry?.isCompleted || entry?.isError || entry?.isWarning) return;
                delete next[key];
                changed = true;
            });
            return changed ? next : previous;
        });
        return undefined;
    }, [open, liveUsecase?._id, progressStatus]);

    const augmentationItems = useMemo(
        () => collectImproveAugmentationItems({
            progressStatus: sessionProgress,
            usecaseId: liveUsecase?._id,
            currentDatasets: liveUsecase?.datasets,
            baselineDatasets,
        }),
        [sessionProgress, liveUsecase?._id, liveUsecase?.datasets, baselineDatasets],
    );

    const handleClose = () => {
        if (submitting) return;
        onClose?.();
    };

    const handleTablePopoverChange = useCallback((isOpen) => {
        tablePopoverOpenRef.current = Boolean(isOpen);
    }, []);

    const handlePortalClose = () => {
        if (tablePopoverOpenRef.current || suppressWizardCloseRef.current) {
            suppressWizardCloseRef.current = false;
            return;
        }
        handleClose();
    };

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDownCapture = (event) => {
            if (!tablePopoverOpenRef.current || !isSparkModalMask(event.target)) return;
            suppressWizardCloseRef.current = true;
        };
        const onClickCapture = (event) => {
            if (!isSparkModalMask(event.target)) return;
            if (tablePopoverOpenRef.current || suppressWizardCloseRef.current) {
                event.stopPropagation();
                suppressWizardCloseRef.current = false;
            }
        };
        document.addEventListener('pointerdown', onPointerDownCapture, true);
        document.addEventListener('click', onClickCapture, true);
        return () => {
            document.removeEventListener('pointerdown', onPointerDownCapture, true);
            document.removeEventListener('click', onClickCapture, true);
        };
    }, [open]);

    const handleNext = () => {
        if (
            annotationMode === 'human'
            && !areAllClassificationReviewGoalsMet(humanReviewProgress, allLabelOptions)
        ) {
            setFocusIncompleteToken((token) => token + 1);
            sparkToast.error('Review at least 15 examples per label before continuing.');
            return;
        }
        setStep('augmentation');
    };

    const handleHumanRetrain = () => {
        if (!liveUsecase) return;
        setSubmitting(true);
        startTrainingProcess(
            liveUsecase,
            defaultTrainingParamsForUsecase(liveUsecase),
            () => {},
            () => {
                setSubmitting(false);
                onClose?.();
            },
            (errorDetail) => {
                setSubmitting(false);
                sparkToast.error(errorDetail || 'Failed to start training');
            },
            { improvement_methods: ['human'] },
        );
    };

    const handleAiRetrain = () => {
        if (!liveUsecase) return;
        setSubmitting(true);
        const payload = {
            client_id: liveUsecase.client_id,
            usecase_id: liveUsecase._id,
            prompt,
            notify_email: emailMe,
            improvement_methods: ['ai'],
        };
        if (hasPromptText(liveUsecase?.teacher_system_prompt)) {
            payload.teacher_system_prompt = systemPrompt;
        }
        data_from_server_post(
            'improve-and-retrain',
            payload,
            (result) => {
                setSubmitting(false);
                if (result?.success) {
                    if (!result.started_training) {
                        updateUsecase?.(liveUsecase._id, {
                            improve_retrain_pending: result.improve_retrain_pending || {
                                remaining_datasets: result.queued_datasets || [],
                                retry_datasets: [],
                                awaiting_continue: false,
                                improvement_methods: ['ai'],
                            },
                        });
                    }
                    onClose?.();
                    return;
                }
                sparkToast.error(result?.detail || result?.message || 'Failed to start improve and retrain');
            },
            (errorDetail) => {
                setSubmitting(false);
                sparkToast.error(errorDetail || 'Failed to start improve and retrain');
            },
        );
    };

    const handlePrimary = () => {
        if (step === 'annotate') {
            handleNext();
            return;
        }
        if (annotationMode === 'ai') {
            handleAiRetrain();
            return;
        }
        handleHumanRetrain();
    };

    const primaryLabel = step === 'annotate' ? 'Next' : 'Improve & Retrain';
    const tertiaryLabel = step === 'annotate' ? 'Cancel' : 'Back';
    const showEmailSlot = step === 'annotate' && annotationMode === 'ai';

    return (
        <>
            <SparkModalPortal
                open={open}
                onClose={handlePortalClose}
                size="slot"
                mask={!nestedOverlayOpen}
                keyboard={!nestedOverlayOpen}
                hidden={nestedOverlayOpen}
                destroyOnClose
            >
                <Modal
                    title="Start Improving Your Model"
                    showSubtitle={false}
                    showScrollBar
                    showFooter
                    showClose
                    showOverlay={false}
                    showTertiaryButton
                    fixedHeight={step === 'annotate'}
                    scrollResetKey={step}
                    tertiaryLabel={tertiaryLabel}
                    primaryLabel={primaryLabel}
                    primaryIcon={
                        submitting && step === 'augmentation'
                            ? <SparkIcon slug="loader" size="small" className="spark-icon-spin" />
                            : undefined
                    }
                    primaryDisabled={submitting}
                    primaryDisableWhenReadOnly={step === 'augmentation'}
                    showFooterSlot={showEmailSlot}
                    footerSlot={
                        showEmailSlot ? (
                            <Checkbox
                                label="Email me when complete"
                                status={emailMe ? 'active' : 'inactive'}
                                onChange={() => setEmailMe((previous) => !previous)}
                            />
                        ) : null
                    }
                    onCloseClick={handleClose}
                    onTertiaryClick={step === 'annotate' ? handleClose : () => setStep('annotate')}
                    onPrimaryClick={handlePrimary}
                >
                    <div style={STACK_STYLE}>
                        {step === 'annotate' ? (
                            <>
                                <Text variant="heading-5-semibold">Labels Annotation</Text>
                                <div style={ANNOTATE_MODE_STYLE}>
                                    <div style={ANNOTATE_TITLE_ROW_STYLE}>
                                        <Label label="Annotate Labels" />
                                        <TooltipTrigger text="Choose Human to review labels yourself, or AI to regenerate labels from your task definition.">
                                            <span>
                                                <SparkIcon slug="circle-help" size="small" />
                                            </span>
                                        </TooltipTrigger>
                                    </div>
                                    <SegmentedControl
                                        items={ANNOTATE_ITEMS}
                                        activeId={annotationMode}
                                        onChange={setAnnotationMode}
                                        size="small"
                                    />
                                    {annotationMode === 'human' ? (
                                        <Text variant="detail-regular" color="secondary">
                                            Review at least 15 examples{' '}
                                            <Text as="span" variant="detail-semibold">
                                                per label
                                            </Text>{' '}
                                            for optimal results.
                                        </Text>
                                    ) : null}
                                </div>
                                {annotationMode === 'human' ? (
                                    <ImproveFlowHumanStep
                                        usecase={liveUsecase}
                                        dataset={dataset}
                                        metadata={datasetMetadata}
                                        availableExtraParamsKeys={availableExtraParamsKeys?.dataset || []}
                                        crossReferenceFields={crossReferenceFields}
                                        isLoading={isDatasetLoading}
                                        handleTableChange={handleTableChange}
                                        setIsInDB={setIsInDB}
                                        currentFilters={currentFilters}
                                        activeSorter={activeSorter}
                                        setCurrentFilters={setCurrentFilters}
                                        setActiveSorter={setActiveSorter}
                                        allLabelOptions={allLabelOptions}
                                        refreshCategories={() => {
                                            data_from_server_post('get-labels', liveUsecase._id, (labels) => {
                                                setAllLabelOptions(labels || {});
                                            });
                                        }}
                                        updateRecord={updateRecord}
                                        onNestedOverlayChange={setTableOverlayOpen}
                                        showTourOnOpen={!improveTourDismissed}
                                        onTourClose={() => setImproveTourDismissed(true)}
                                        onValidationProgressChange={setHumanReviewProgress}
                                        onTablePopoverChange={handleTablePopoverChange}
                                        focusIncompleteToken={focusIncompleteToken}
                                    />
                                ) : (
                                    <ImproveFlowAiStep
                                        usecase={liveUsecase}
                                        prompt={prompt}
                                        onPromptChange={setPrompt}
                                        systemPrompt={systemPrompt}
                                        onSystemPromptChange={setSystemPrompt}
                                        totalRecords={datasetMetadata?.num_records}
                                    />
                                )}
                            </>
                        ) : (
                            <ImproveFlowAugmentationStep
                                items={augmentationItems}
                                onOpenAddData={(source) => setAddDataSource(source)}
                                onFilesSelected={(files) => {
                                    setDroppedFiles(files);
                                    setAddDataSource('upload');
                                }}
                            />
                        )}
                    </div>
                </Modal>
            </SparkModalPortal>
            <AddDataModal
                open={addDataOpen}
                onClose={() => {
                    setAddDataSource(null);
                    setDroppedFiles(null);
                }}
                source={addDataSource}
                uploadTarget="dataset"
                usecase={liveUsecase}
                initialDroppedFiles={droppedFiles}
                onInitialDroppedFilesConsumed={() => setDroppedFiles(null)}
                onSuccess={() => {
                    setAddDataSource(null);
                    setDroppedFiles(null);
                }}
            />
        </>
    );
}
